import '../../platform.css';
import '../../components/documents/documents.css';
import './archive.css';

import { useRecoilValue } from 'recoil';
import { useEffect, useState } from 'react';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { playerOverviewsState } from '../../recoil/atoms/playerOverviewsState';
import { navigationPropsState } from '../../recoil/atoms/navigationPropsState';
import { reportFiltersState } from '../../recoil/atoms/reportFiltersState';
import { useSetNavigationProps } from '../../recoil/hooks/useSetNavigationProps';
import { useGetAndAddPlayerOverview } from '../../recoil/hooks/useGetAndAddPlayerOverview';
import { getNewPlayerOverviewsForArchivedTeams } from '../../services/server/application/playerOverviews';
import { useOpenGlobalModal } from '../../recoil/hooks/useOpenGlobalModal';
import { useAuthContext } from '../../../common/contexts/useAuthContext';

import FolderSharedIcon from '@mui/icons-material/FolderShared';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';

import Fuse from 'fuse.js';

import { getAllReports } from '../../services/firestore/reports';
import { ViewContainerBackgroundSvg } from '../../../assets/svg/ViewContainerBackgroundSvg';
import { Report } from '../../components/documents/report/Report';
import { TeamView } from '../../components/teamView/TeamView';
import { ReportFilters } from './ReportFilters';
import { ReportStatistics } from './ReportStatistics';
import { ExistingArchivedTeams } from '../../components/documents/existingDocuments/ExistingArchivedTeams';
import { ExistingReports } from '../../components/documents/existingDocuments/ExistingReports';
import { FixedTabs } from '../../components/controls/navigation/FixedTabs';
import { getAllArchivedTeams, updateArchivedTeamIsDeletedStatus } from '../../services/firestore/archivedTeams';
import { PlayerId, PlayerOverview, PlayerOverviews } from '../../types/player';
import { PlayerReport } from '../../types/report';
import { ArchivedTeam } from '../../types/team';
import { StringToElementMap } from '../../types/common';


export const Archive = () => {

  const { currentUser } = useAuthContext();
  const userConfig = useRecoilValue(userConfigState);

  const { openConfirmModal } = useOpenGlobalModal();

  const playerOverviews = useRecoilValue(playerOverviewsState);
  const getAndAddPlayerOverview = useGetAndAddPlayerOverview();

  const [additionalPlayerOverviews, setAdditionalPlayerOverviews] = useState<PlayerOverviews>({});

  const reportFilters = useRecoilValue(reportFiltersState);
  const [currentReportFilterExpanded, setCurrentReportFilterExpanded] = useState<string | undefined>(undefined);

  const [allReports, setAllReports] = useState<PlayerReport[]>([]);
  const [reportsToShow, setReportsToShow] = useState<PlayerReport[]>([]);
  const [isReportsLoading, setIsReportsLoading] = useState<boolean>(true);
  const [selectedReport, setSelectedReport] = useState<PlayerReport | undefined>(undefined);

  const [archivedTeams, setArchivedTeams] = useState<ArchivedTeam[]>([]);
  const [isArchivedTeamsLoading, setIsArchivedTeamsLoading] = useState<boolean>(true);
  const [selectedArchivedTeam, setSelectedArchivedTeam] = useState<ArchivedTeam | undefined>(undefined);

  const [selectedPlayerOverview, setSelectedPlayerOverview] = useState<PlayerOverview | undefined>(undefined);
  const [isLoadingSelectedPlayerOverview, setIsLoadingSelectedPlayerOverview] = useState<boolean>(false);


  const hasAccessToReports = userConfig?.accessConfig.reportAccess !== 'none';

  const tabOptions = ['archivedTeams'];
  if (hasAccessToReports) {
    tabOptions.unshift('reports');
  }

  const tabOptionToTabIcon: StringToElementMap = {
    'reports': <FolderSharedIcon key={'folder-shared-icon'} style={{ fontSize: 21, marginTop: 1 }} />,
    'archivedTeams': <FolderSpecialIcon key={'folder-special-icon'} style={{ fontSize: 21, marginTop: 1 }} />
  };


  const navigationProps = useRecoilValue(navigationPropsState);
  const activeSubTab = navigationProps.activeArchiveSubTab ?? 0;
  const updateNavigationProps = useSetNavigationProps();
  const setActiveSubTab = (subTab: number) => {
    updateNavigationProps({ activeArchiveSubTab: subTab }, true);
  };


  const handleSetSelectedPlayerOverview = async (playerId: PlayerId) => {

    setSelectedPlayerOverview(undefined);

    const numberId = Number(playerId);

    if (isNaN(numberId)) {
      return;
    }

    if (playerOverviews[numberId]) {
      setSelectedPlayerOverview(playerOverviews[numberId]);
    }
    else if (additionalPlayerOverviews[numberId]) {
      setSelectedPlayerOverview(additionalPlayerOverviews[numberId]);
    }
    else {
      setIsLoadingSelectedPlayerOverview(true);
      const playerOverview = await getAndAddPlayerOverview(numberId, currentUser);
      setSelectedPlayerOverview(playerOverview);
      setIsLoadingSelectedPlayerOverview(false);
    }
  };


  const openConfirmLeaveReportModal = (action: () => void) => {
    openConfirmModal(
      action,
      'exitReport?',
      'exitReportInfo'
    );
  };


  const handleSetActiveSubTab = (subTab: number) => {
    setActiveSubTab(subTab);
    setSelectedReport(undefined);
    setSelectedArchivedTeam(undefined);
    setSelectedPlayerOverview(undefined);
  };

  const handleSubTabClick = (subTab: number) => {
    if (navigationProps.reportHasUnsavedChanges) {
      openConfirmLeaveReportModal(() => handleSetActiveSubTab(subTab));
    }
    else {
      handleSetActiveSubTab(subTab);
    }
  };


  const handleCloseReport = () => {
    setSelectedReport(undefined);
    setSelectedPlayerOverview(undefined);
    updateNavigationProps({ reportHasUnsavedChanges: false });
  };

  const handleReportCloseIconClick = () => {
    if (navigationProps.reportHasUnsavedChanges) {
      openConfirmLeaveReportModal(() => handleCloseReport());
    }
    else {
      handleCloseReport();
    }
  };


  const handleSelectReport = (report: PlayerReport | undefined) => {
    setSelectedReport(report);
    updateNavigationProps({ reportHasUnsavedChanges: false });
    if (report) {
      handleSetSelectedPlayerOverview(report.playerId);
    }
  };

  const handleReportClicked = (report: PlayerReport | undefined) => {
    if (navigationProps.reportHasUnsavedChanges) {
      openConfirmLeaveReportModal(() => handleSelectReport(report));
    }
    else {
      handleSelectReport(report);
    }
  };


  const updateReport = (report: PlayerReport) => {
    let reportWasDeleted = false;

    const updatedReports = allReports.map((existingReport: PlayerReport) => {
      if (existingReport.id === report.id) {

        if (!existingReport.isDeleted && report.isDeleted) {
          reportWasDeleted = true;
        }

        return report;
      }

      return existingReport;
    });

    setAllReports(updatedReports);
    setSelectedReport(reportWasDeleted ? undefined : report);
    updateNavigationProps({ reportHasUnsavedChanges: false });
  };


  const deleteOrRestoreArchivedTeam = async () => {
    if (!userConfig || !selectedArchivedTeam?.id) return;

    const updatedArchivedTeam = await updateArchivedTeamIsDeletedStatus(
      selectedArchivedTeam.id,
      !selectedArchivedTeam.isDeleted,
      userConfig.club,
      currentUser
    );

    if (updatedArchivedTeam) {
      setArchivedTeams(archivedTeams.map(team => {
        if (team.id === selectedArchivedTeam.id) {
          team.isDeleted = !team.isDeleted;
        }
        return team;
      }));
    }

    if (selectedArchivedTeam.isDeleted) {
      setSelectedArchivedTeam(undefined);
    }
  };


  useEffect(() => {
    if (userConfig) {
      getAllReports(userConfig, currentUser).then((allReports: PlayerReport[]) => {
        setAllReports(allReports);
        setIsReportsLoading(false);
      });

      getAllArchivedTeams(userConfig, currentUser).then((allArchivedTeams: ArchivedTeam[]) => {
        setArchivedTeams(allArchivedTeams);
        setIsArchivedTeamsLoading(false);
      });
    }
  }, [currentUser, userConfig]);


  useEffect(() => {
    if (!isArchivedTeamsLoading && archivedTeams.length > 0 && playerOverviews) {
      getNewPlayerOverviewsForArchivedTeams(archivedTeams, playerOverviews, currentUser).then((newPlayerOverviews: PlayerOverviews) => {
        setAdditionalPlayerOverviews(newPlayerOverviews);
      });
    }
  }, [archivedTeams, currentUser, isArchivedTeamsLoading, playerOverviews]);


  useEffect(() => {
    let filteredReports = allReports.filter(report => {
      const reportMatchesUserFilter = reportFilters.selectedUsers.length === 0 || reportFilters.selectedUsers.includes(report.userEmail);
      const reportMatchesReportTypeFilter = reportFilters.selectedReportTypes.length === 0 || reportFilters.selectedReportTypes.includes(report.reportType);
      const reportMatchesMinimumOverallRatingFilter = reportFilters.selectedMinimumOverallRating.length === 0 || (
        report.reportFields?.overallScore && report.reportFields.overallScore >= reportFilters.selectedMinimumOverallRating[0]
      );
      return reportMatchesUserFilter && reportMatchesReportTypeFilter && reportMatchesMinimumOverallRatingFilter;
    });

    if (reportFilters.nameSearchString) {
      const fuse = new Fuse(filteredReports, { keys: ['playerName'], threshold: 0.2, shouldSort: false });
      filteredReports = fuse.search(reportFilters.nameSearchString).map(result => result.item);
    }

    setReportsToShow(filteredReports);
  }, [allReports, reportFilters]);


  return (
    <div className='platform-view-section'>

      {!selectedArchivedTeam && (
        <div className='full-size'>
          <ViewContainerBackgroundSvg />
        </div>
      )}

      {currentReportFilterExpanded && <div className='filter-section-empty-background' onClick={() => setCurrentReportFilterExpanded(undefined)} />}

      <FixedTabs
        tabOptions={tabOptions}
        activeTab={activeSubTab}
        setActiveTab={handleSubTabClick}
        tabIcons={tabOptions.map(tabOption => tabOptionToTabIcon[tabOption])}
        parentTab={'archive'}
      />

      <div className='platform-view-content-container-with-tabs'>

        {tabOptions[activeSubTab] === 'reports' && (
          <div className='full-size-container'>
            {!isReportsLoading && (
              <div className='archive-existing-reports-container fade-in'>
                <ExistingReports
                  reports={reportsToShow}
                  selectedReport={selectedReport}
                  handleReportClicked={handleReportClicked}
                  isPlayerView={false}
                />
              </div>
            )}
            <div className='archive-report-container'>
              {selectedReport && (
                <Report
                  selectedReport={selectedReport}

                  handleCloseIconClick={handleReportCloseIconClick}
                  addOrUpdateReport={updateReport}

                  playerOverview={selectedPlayerOverview}
                  playerEntry={{ id: selectedReport.playerId, name: selectedReport.playerName }}

                  isArchiveView={true}
                  isLoadingSelectedPlayerOverview={isLoadingSelectedPlayerOverview}
                />
              )}

              {!selectedReport && !isReportsLoading && (
                <div className='archive-report-filters-and-statistics-container'>
                  <ReportFilters
                    currentReportFilterExpanded={currentReportFilterExpanded}
                    setCurrentReportFilterExpanded={setCurrentReportFilterExpanded}
                  />

                  <div className='archive-report-filters-divider' />

                  <ReportStatistics
                    reports={reportsToShow}
                  />
                </div>
              )}
            </div>
          </div>
        )}


        {tabOptions[activeSubTab] === 'archivedTeams' && !isArchivedTeamsLoading && (
          <div className='full-size-container'>
            <div className='archive-archived-teams-container fade-in'>
              <ExistingArchivedTeams
                archivedTeams={archivedTeams}
                selectedArchivedTeam={selectedArchivedTeam}
                handleArchivedTeamClicked={setSelectedArchivedTeam}
              />
            </div>
            {selectedArchivedTeam && (
              <div className='logistic-team-view-section archived-team-view-section'>
                <TeamView
                  teamId={'archivedTeam'}
                  team={selectedArchivedTeam}
                  isArchivedTeamDeleted={selectedArchivedTeam['isDeleted']}
                  closeArchivedTeam={() => setSelectedArchivedTeam(undefined)}
                  deleteOrRestoreArchivedTeam={deleteOrRestoreArchivedTeam}
                  additionalPlayerOverviews={additionalPlayerOverviews}
                />
              </div>
            )}
          </div>
        )}

      </div>

    </div>
  );
};
